import { useCallback, useEffect, useMemo, useState } from 'react'
import { ProgressReportByTeamPageConfig } from '../../../../../domain/entity/version-report/VersionReportTemplateEntity'
import {
  ProgressReportByTeamPageContent,
  ProgressReportQualitativeContent,
} from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { ProgressReportTable } from '../table/ProgressReportTable'
import { ProgressValue } from '../../../../../domain/value-object/ProjectPlanSnapshot'
import { useProjectPlanSnapshotRepository } from '../../../../../services/adaptors/projectPlanSnapshotRepositoryAdaptor'
import { aggregatedValueService } from '../../../../../domain/value-object/AggregatedValueVO'
import {
  AggregateField,
  WbsItemType,
} from '../../../../../domain/entity/WbsItemEntity'
import { EditConfigButton } from '.'
import { ProgressReportByTeamConfigDialog } from '../dialog/ProgressReportByTeamConfigDialog'
import { dateV2Service } from '../../../../../domain/value-object/DateV2'
import { useTeamReportColumnDef } from '../../hooks/columnDef'
import { useDialogState } from '../../../../components/dialogs/useDialogState'

type ProgressReportByTeamProps = {
  snapshotUuid: string
  editing: boolean
  config: ProgressReportByTeamPageConfig
  content: ProgressReportByTeamPageContent
  updatePageConfig: <
    K extends keyof ProgressReportByTeamPageConfig,
    V extends ProgressReportByTeamPageConfig[K],
  >(
    path: K,
    value: V
  ) => void
  updatePageContent: <
    K extends keyof ProgressReportByTeamPageContent,
    V extends ProgressReportByTeamPageContent[K],
  >(
    path: K,
    value: V
  ) => void
}
export const ProgressReportByTeam = ({
  editing,
  snapshotUuid,
  config,
  content,
  updatePageConfig,
  updatePageContent,
}: ProgressReportByTeamProps) => {
  const [progressValues, setProgressValues] = useState<ProgressValue[]>([])
  const { pivotByTeamUuids } = useProjectPlanSnapshotRepository()
  useEffect(() => {
    const fn = async () => {
      const response = await pivotByTeamUuids(
        snapshotUuid,
        config.rootUuid,
        config.tableRows
      )
      setProgressValues(response)
    }
    fn()
  }, [config.rootUuid, config.tableRows, pivotByTeamUuids, snapshotUuid])
  const tableContents = useMemo(
    () =>
      progressValues.map(v => {
        const qualitativeContent = content.tableContents.find(
          u => u.uuid === v.uuid
        )
        return {
          uuid: v.uuid,
          name: v.name,
          status: qualitativeContent?.status || 'SUNNY',
          comment: qualitativeContent?.comment || '',
          scheduledProgressRate:
            aggregatedValueService.getFormattedRatePercentage(
              v.planned,
              v.total,
              WbsItemType.TASK,
              AggregateField.WBS_ITEM_COUNT
            ),
          progressRate: aggregatedValueService.getFormattedRatePercentage(
            v.completed,
            v.total,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          total: aggregatedValueService.getFormatted(
            v.total,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          planned: aggregatedValueService.getFormatted(
            v.planned,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          completed: aggregatedValueService.getFormatted(
            v.completed,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          delay: aggregatedValueService.getFormatted(
            v.delay,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          remaining: aggregatedValueService.getFormatted(
            v.remaining,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          wbsItemStatus: v.wbsItemStatus,
          team: v.team,
          progressRateDiff: aggregatedValueService.getFormattedRatePercentage(
            v.plannedCompletedDiff,
            v.total,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          scheduledStartDate: dateV2Service
            .fromString(v.scheduledDate?.startDate)
            ?.toDateString(),
          scheduledEndDate: dateV2Service
            .fromString(v.scheduledDate?.endDate)
            ?.toDateString(),
          maxChildScheduledEndDate: v.maxChildScheduledEndDate,
          preceding: aggregatedValueService.getFormatted(
            v.preceding,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          unplanned: aggregatedValueService.getFormatted(
            v.unplanned,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
        }
      }),
    [content.tableContents, progressValues]
  )
  const qualitativeContents: ProgressReportQualitativeContent[] = useMemo(
    () =>
      tableContents.map(v => ({
        uuid: v.uuid,
        status: v.status,
        comment: v.comment,
      })),
    [tableContents]
  )
  const updateTableContent = useCallback(
    (v: ProgressReportQualitativeContent, index: number) => {
      const newContent = qualitativeContents.map((u, i) => {
        if (i !== index) return u
        return v
      })
      updatePageContent('tableContents', newContent)
    },
    [qualitativeContents, updatePageContent]
  )
  const updateConfig = useCallback(
    (rootUuid: string | undefined, teamUuids: string[]) => {
      updatePageConfig('tableRows', teamUuids)
      updatePageConfig('rootUuid', rootUuid)
    },
    [updatePageConfig]
  )
  const [open, openDialog, closeDialog] = useDialogState()
  const columns = useTeamReportColumnDef({
    editing,
    updateTableContent,
  })
  return (
    <>
      <ProgressReportTable
        columns={columns}
        editing={editing}
        contents={tableContents}
      />
      <EditConfigButton editing={editing} onClick={openDialog} />
      <ProgressReportByTeamConfigDialog
        open={open}
        onClose={closeDialog}
        updateConfig={updateConfig}
        config={config}
        snapshotUuid={snapshotUuid}
      />
    </>
  )
}
